import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface InviteDetails {
  email: string;
  role: string;
  schoolName: string;
  schoolId: string;
}

function InviteStyles() {
  return (
    <style>{`
      .invite-page {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
        min-height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 20px;
      }

      .invite-card {
        background: white;
        border-radius: 20px;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
        width: 100%;
        max-width: 460px;
        padding: 2.5rem 2rem;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .invite-logo {
        width: 52px;
        height: 52px;
        background: linear-gradient(135deg, #4f46e5, #7c3aed);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 12px;
      }

      .invite-title {
        font-size: 1.7rem;
        color: #1e1b4b;
        margin-bottom: 4px;
        font-weight: 700;
        text-align: center;
      }

      .invite-subtitle {
        font-size: 0.9rem;
        color: #71717a;
        margin-bottom: 16px;
        text-align: center;
      }

      .invite-info-box {
        width: 100%;
        max-width: 380px;
        padding: 12px 16px;
        background: #f5f3ff;
        border: 1px solid #e0e7ff;
        border-radius: 12px;
        margin-bottom: 12px;
      }

      .invite-info-box p {
        font-size: 0.85rem;
        color: #4338ca;
        margin: 2px 0;
      }

      .invite-info-box strong {
        color: #1e1b4b;
      }

      .invite-error {
        width: 100%;
        max-width: 380px;
        padding: 8px 14px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 0.8rem;
        margin-bottom: 8px;
        text-align: center;
      }

      .invite-success {
        width: 100%;
        max-width: 380px;
        padding: 8px 14px;
        background: #f0fdf4;
        border: 1px solid #bbf7d0;
        border-radius: 8px;
        color: #16a34a;
        font-size: 0.8rem;
        margin-bottom: 8px;
        text-align: center;
      }

      .invite-form {
        width: 100%;
        max-width: 380px;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .invite-input-field {
        max-width: 380px;
        width: 100%;
        background-color: #f5f3ff;
        margin: 5px 0;
        height: 46px;
        border-radius: 46px;
        display: flex;
        align-items: center;
        padding: 0 1.2rem;
        position: relative;
        transition: 0.3s;
        border: 2px solid transparent;
      }

      .invite-input-field:focus-within {
        background-color: #ede9fe;
        border-color: #4f46e5;
      }

      .invite-input-field i {
        color: #666;
        font-size: 1rem;
        margin-right: 0.5rem;
        flex-shrink: 0;
        width: 20px;
        text-align: center;
      }

      .invite-input-field input {
        background: none;
        outline: none;
        border: none;
        line-height: 1;
        font-weight: 500;
        font-size: 0.95rem;
        color: #333;
        width: 100%;
        flex: 1;
      }

      .invite-input-field input::placeholder {
        color: #a1a1aa;
        font-weight: 400;
      }

      .invite-input-field .toggle-pw {
        background: none;
        border: none;
        cursor: pointer;
        color: #71717a;
        padding: 4px;
        display: flex;
        align-items: center;
        flex-shrink: 0;
      }

      .invite-input-field .toggle-pw:hover {
        color: #4f46e5;
      }

      .invite-btn {
        width: 220px;
        background: linear-gradient(135deg, #4f46e5, #7c3aed);
        border: none;
        outline: none;
        height: 46px;
        border-radius: 46px;
        color: #fff;
        text-transform: uppercase;
        font-weight: 600;
        margin: 12px 0 8px;
        cursor: pointer;
        transition: 0.4s;
        font-size: 0.8rem;
        letter-spacing: 0.5px;
      }

      .invite-btn:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(79, 70, 229, 0.4);
      }

      .invite-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .invite-link {
        background: none;
        border: none;
        color: #4f46e5;
        font-size: 0.85rem;
        cursor: pointer;
        text-decoration: underline;
        margin-top: 8px;
      }

      .invite-link:hover {
        color: #4338ca;
      }

      .invite-link-text {
        font-size: 0.85rem;
        color: #71717a;
        margin-top: 8px;
      }

      .invite-spinner {
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-top: 3px solid white;
        border-radius: 50%;
        width: 18px;
        height: 18px;
        animation: invite-spin 0.8s linear infinite;
        display: inline-block;
        margin-right: 8px;
        vertical-align: middle;
      }

      @keyframes invite-spin {
        to { transform: rotate(360deg); }
      }

      .invite-icon-circle {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
      }

      .invite-icon-circle.error { background: #fef2f2; }
      .invite-icon-circle.success { background: #f0fdf4; }

      @media (max-width: 570px) {
        .invite-card {
          padding: 2rem 1.2rem;
          border-radius: 16px;
        }

        .invite-title {
          font-size: 1.4rem;
        }

        .invite-input-field {
          height: 44px;
          padding: 0 1rem;
        }

        .invite-input-field i {
          display: none;
        }

        .invite-btn {
          height: 42px;
          font-size: 0.75rem;
        }
      }
    `}</style>
  );
}

export function InviteAccept() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invite, setInvite] = useState<InviteDetails | null>(null);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invite token provided');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/invite/${token}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Invalid invite');
        return;
      }

      setInvite(data.invite);
      setName(data.invite.email.split('@')[0]); // Default name from email
    } catch (_err) {
      setError('Failed to validate invite. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/auth/invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, name }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to create account');
        return;
      }

      setSuccess(true);

      const userRole = data.user?.role || invite?.role || 'student';

      // Store auth token in sessionStorage (scoped to tab, cleared on close)
      if (data.access_token) {
        sessionStorage.setItem('authToken', data.access_token);
        sessionStorage.setItem('refreshToken', data.refresh_token || '');
      }
      localStorage.setItem('userRole', userRole);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Notify Electron so it can start socket connection + screen monitoring
      if (window.electronAPI?.setUserRole && data.access_token) {
        window.electronAPI.setUserRole({ role: userRole, token: data.access_token });
      }

      // Redirect to role-specific route (these use UserRoute/SchoolAdminRoute which check localStorage, not Supabase client auth)
      setTimeout(() => {
        if (data.access_token) {
          // Have auth token — go directly to role dashboard
          if (userRole === 'teacher') {
            window.location.href = '/teacher/dashboard';
          } else if (userRole === 'student') {
            window.location.href = '/student/dashboard';
          } else if (userRole === 'admin') {
            window.location.href = '/admin';
          } else {
            window.location.href = '/dashboard';
          }
        } else {
          // No auth token — user needs to log in manually
          window.location.href = '/login';
        }
      }, 2000);
    } catch (_err) {
      setError('Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <InviteStyles />
        <div className="invite-page">
          <div className="invite-card">
            <div className="invite-logo">
              <Shield size={28} color="#fff" />
            </div>
            <div className="invite-spinner" style={{ borderTopColor: '#4f46e5', borderColor: 'rgba(79,70,229,0.2)', width: 32, height: 32, marginBottom: 12 }} />
            <p style={{ color: '#71717a', fontSize: '0.9rem' }}>Validating invite...</p>
          </div>
        </div>
      </>
    );
  }

  if (error && !invite) {
    return (
      <>
        <InviteStyles />
        <div className="invite-page">
          <div className="invite-card">
            <div className="invite-icon-circle error">
              <XCircle size={32} color="#dc2626" />
            </div>
            <h2 className="invite-title" style={{ fontSize: '1.4rem' }}>Invalid Invite</h2>
            <p style={{ color: '#71717a', fontSize: '0.9rem', textAlign: 'center', marginBottom: 20 }}>{error}</p>
            <button className="invite-btn" onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  if (success) {
    const isStudent = invite?.role === 'student';
    return (
      <>
        <InviteStyles />
        <div className="invite-page">
          <div className="invite-card">
            <div className="invite-icon-circle success">
              <CheckCircle size={32} color="#16a34a" />
            </div>
            <h2 className="invite-title" style={{ fontSize: '1.4rem' }}>Account Created!</h2>
            <p style={{ color: '#71717a', fontSize: '0.9rem', textAlign: 'center' }}>
              Your account has been set up successfully.
            </p>
            <p style={{ color: '#a1a1aa', fontSize: '0.8rem', marginTop: 8 }}>
              {isStudent ? 'Taking you to onboarding...' : 'Redirecting to your dashboard...'}
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <InviteStyles />
      <div className="invite-page">
        <div className="invite-card">
          <div className="invite-logo">
            <Shield size={28} color="#fff" />
          </div>
          <h2 className="invite-title">Welcome to <br/>ShieldEd Haven</h2>
          <p className="invite-subtitle">
            Complete your account setup for {invite?.schoolName}
          </p>

          <div className="invite-info-box">
            <p><strong>Email:</strong> {invite?.email}</p>
            <p><strong>Role:</strong> {invite?.role?.charAt(0).toUpperCase()}{invite?.role?.slice(1)}</p>
          </div>

          {error && <div className="invite-error">{error}</div>}

          <form className="invite-form" onSubmit={handleSubmit}>
            <div className="invite-input-field">
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="invite-input-field">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <button
                type="button"
                className="toggle-pw"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <div className="invite-input-field">
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="invite-btn" disabled={submitting}>
              {submitting ? (
                <><span className="invite-spinner" />Creating...</>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <p className="invite-link-text">
            Already have an account?{' '}
            <button className="invite-link" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </p>
        </div>
      </div>
    </>
  );
}
